/** Scroll distance over a section boundary while the top bar color blends */
export const TOPBAR_SECTION_BLEND_PX = 60;

/** Wechsel ~24px vor Kontakt der unteren Top-Bar-Kante mit der Section-Grenze */
export const TOPBAR_BLEND_LEAD_PX = 24;

function parseHex(hex: string) {
  const n = hex.replace('#', '');
  return {
    r: parseInt(n.substring(0, 2), 16),
    g: parseInt(n.substring(2, 4), 16),
    b: parseInt(n.substring(4, 6), 16),
  };
}

export function lerpHex(a: string, b: string, t: number): string {
  const c1 = parseHex(a);
  const c2 = parseHex(b);
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const bVal = Math.round(c1.b + (c2.b - c1.b) * t);
  return `rgb(${r}, ${g}, ${bVal})`;
}

export type TopBarTheme = 'light' | 'dark';

function clamp01(v: number) {
  return Math.min(1, Math.max(0, v));
}

export function getTopBarAppearance(
  contentEl: HTMLElement | null,
  probeY: number,
  fallbackBg: string,
): { bg: string; theme: TopBarTheme } {
  if (!contentEl) return { bg: fallbackBg, theme: 'light' };

  const sections = Array.from(
    contentEl.querySelectorAll<HTMLElement>('[data-section-bg]'),
  );
  if (!sections.length) return { bg: fallbackBg, theme: 'light' };

  const probe = probeY + TOPBAR_BLEND_LEAD_PX;
  const halfBlend = TOPBAR_SECTION_BLEND_PX / 2;

  for (let i = 0; i < sections.length - 1; i++) {
    const boundaryY = sections[i + 1].getBoundingClientRect().top;
    const dist = Math.abs(boundaryY - probe);
    if (dist < halfBlend) {
      const t = clamp01((probe + halfBlend - boundaryY) / TOPBAR_SECTION_BLEND_PX);
      const bgAbove = sections[i].dataset.sectionBg || fallbackBg;
      const bgBelow = sections[i + 1].dataset.sectionBg || fallbackBg;
      const themeAbove = (sections[i].dataset.sectionTheme as TopBarTheme) || 'light';
      const themeBelow = (sections[i + 1].dataset.sectionTheme as TopBarTheme) || 'light';
      return {
        bg: lerpHex(bgAbove, bgBelow, t),
        theme: t >= 0.5 ? themeBelow : themeAbove,
      };
    }
  }

  for (let i = 0; i < sections.length; i++) {
    const rect = sections[i].getBoundingClientRect();
    if (rect.top <= probe && rect.bottom > probe) {
      return {
        bg: sections[i].dataset.sectionBg || fallbackBg,
        theme: (sections[i].dataset.sectionTheme as TopBarTheme) || 'light',
      };
    }
  }

  const last = sections[sections.length - 1];
  return {
    bg: last.dataset.sectionBg || fallbackBg,
    theme: (last.dataset.sectionTheme as TopBarTheme) || 'light',
  };
}
